<template>
  <div class="about">
    <h1>What is this?</h1>
    <p>
      <b>Let's see</b> is an experimental eye trainer and an active experiment by
      <a target="_blank" href="https://igorstumberger.com">Igor</a> aiming to improve eyesight with daily training.
    </p>

    <h2>THE BACKSTORY</h2>
    <p>It all started when a recurring struggle to read movie subtitles sparked a thought: what if our screen-heavy
      lifestyles were limiting our eyes' ability to focus on distant objects? That simple question led to a deeper
      curiosity about how eye focus and distance might relate to deteriorating eyesight.</p>

    <div class="eyes1">
      <img src="@/assets/img/lets-see-blink.gif" alt="Squinting eye" />
    </div>

    <p>The idea started evolving. Maybe placing focus points (like notes or words) around the room could help stimulate
      the eyes. That eventually turned into a concept for a simple digital tool that shows words at large sizes on a
      screen, which the user could move around to test and train their vision.</p>
    <p>As I often do, I dove right into building a prototype. It was simple enough to put together a quick demo that
      same evening. What you're seeing now is an extended version of that early idea, just presented a bit more cleanly.
    </p>

    <h2>What does it do?</h2>
    <p>It's fairly simple. The app displays a word and its dictionary description in large size on a display of your
      choice.</p>
    <p>You can then place the device somewhere slightly out of reach, but close enough that you can still read the main
      word. Once that becomes too easy, move the device just a bit farther away and try again.</p>
    <p>Every 15 minutes, the app displays a new word, prompting you to read it. If you can read it easily, increase the
      distance again. Of course, there are limits to what your eyes can do, but the idea is to gently push that boundary
      over time.</p>
    <img src="@/assets/img/setup.jpeg" alt="Setup example with an iPad on a bookshelf" class="setup-img">
    <p class="setup-img-caption">Setup example with an iPad on a bookshelf</p>

    <h2>What kind of words will I discover here?</h2>
    <p>I believe the best way to train your eyes is by using unfamiliar words. It makes you focus harder, which increases
      the training effect. And hey, you might pick up a few cool new words along the way!</p>
    <p>
      I pull words from sources like Merriam-Webster's Word of the Day, which is a treasure trove of interesting
      vocabulary. Check out their
      <a target="_blank" href="https://www.merriam-webster.com/word-of-the-day">website</a> for more.
    </p>

    <h2>
      Are you scientists?
      <br />Is this real?
    </h2>
    <p>I'm not a scientist, so everything here is based on personal experience and curiosity, nothing medically
      certified.</p>
    <p>That said, there is chatter around how prolonged near-screen use may affect vision over time, due to lack of
      long-range focus exercise. This app is a small attempt at counterbalancing that trend.</p>
    <p>I must say that I don't take any legal, health, or other responsibility. You're using this tool at your own
      discretion.</p>

    <div class="eyes2">
      <img src="@/assets/img/lets-see-squint.gif" alt="Blinking eye" />
    </div>

    <h2>So...how long until I can throw away my reading glasses?</h2>
    <p>There's no evidence that using this app will let you ditch your glasses, so please don't! Also, you probably look
      amazing in them either way :)</p>

    <h2>
      Sounds great,
      <br />how can I contribute?
    </h2>
    <p>Right now, there's no formal way to track whether this is working. The idea is just to observe your own
      progress by seeing how far you can move the screen over time. If there's enough interest, I might build a more
      structured tracking system and show anonymized results.</p>
    <p>If you're a scientist or researcher with an idea of how this could be tested or expanded, I'd love to hear from
      you.</p>

    <h2>What's with these sponsored words?</h2>
    <p>This was a passion project and fun to build, but it also took quite a bit of time to design and develop. More than
      expected, honestly.</p>
    <p>To support projects like this in the future, I've made room for selected companies to sponsor individual words.
      That means their logo, a bit of info, and a link will appear when their chosen word is shown.</p>
    <p>These banners are clean and minimal, designed to avoid interrupting your eye training experience. I tested so
      many layouts before landing on this one.</p>
    <p>I also made my own simple ad system instead of using third-party trackers, so the experience is cookie-free and
      private. Eyes only.</p>

    <h2 id="sponsors">I'd love to sponsor a word!</h2>
    <p>
      That's amazing! There's a one-time-fee and you keep the bragging rights of "owning" a word forever, I'll even send
      you a certificate you can print and proudly hang on your wall!
    </p>
    <p> If you have a brand or company that wants to be featured, drop me a line <a target="_blank"
        href="mailto:hi@igorstumberger.com?subject=Lets See Sponsorship">here</a> to help fund future experiments like
      this. I'll get back to you as soon as I can. Thanks!
    </p>

    <h2>Were can I find more?</h2>
    <p>
      I'm a product designer who enjoys tinkering with code. You can check out my
      <a target="_blank" href="https://igorstumberger.com">website</a> or follow my ramblings on
      <a target="_blank" href="https://bsky.app/profile/igorstumberger.com">BlueSky</a>.
    </p>
  </div>
</template>


<script>
export default {
  name: "About"
};
</script>

<style scoped>
.about {
  max-width: 500px;
  padding: 120px 20px 50px 20px;
  margin: 0 auto;
  line-height: 1.5;
}

h1 {
  font-family: "OpticianSans", sans-serif;
  font-size: 40px;
  font-weight: 300;
  /* text-align: center; */
  margin-bottom: 10px;
}

h2 {
  font-family: "OpticianSans", sans-serif;
  font-size: 32px;
  font-weight: 300;
  /* text-align: center; */
  margin-bottom: 10px;
  margin-top: 50px;
}

a {
  color: var(--text);
  transition: font-weight 0.25s cubic-bezier(0.075, 0.82, 0.165, 1);
  font-weight: 500;
  border-bottom: 1px dashed var(--text);
  text-decoration: none;
}

a:hover {
  font-weight: 700;
}

a::after {
  content: "";
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-left: 4px;
  background-color: var(--text);
  -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 25"><path fill-rule="evenodd" clip-rule="evenodd" d="M23.775 0.774994L23.7749 20.8251L20.7749 20.8251L20.775 5.89634L2.33568 24.3357L0.214355 22.2143L18.6537 3.77501L3.72494 3.77507L3.72493 0.775069L23.775 0.774994Z"/></svg>') no-repeat center / contain;
  mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 25"><path fill-rule="evenodd" clip-rule="evenodd" d="M23.775 0.774994L23.7749 20.8251L20.7749 20.8251L20.775 5.89634L2.33568 24.3357L0.214355 22.2143L18.6537 3.77501L3.72494 3.77507L3.72493 0.775069L23.775 0.774994Z"/></svg>') no-repeat center / contain;
}

.eyes1 {
  text-align: center;
  padding: 10px 0 30px 0;
}

.eyes2 {
  text-align: center;
  padding-top: 30px;
}

img {
  max-width: 150px;
}

html[data-theme="light"] .eyes1 img,
html[data-theme="light"] .eyes2 img {
  filter: invert(1);
}

.setup-img {
  max-width: 100%;
  border-radius: 10px;
}

.setup-img-caption {
  font-size: 12px;
  color: var(--caption);
  margin-top: 10px;
}
</style>



// WEBPACK FOOTER //
// src/views/About.vue